import { useState, useEffect, useRef } from "react";
import { C, Badge, Btn, Input, Toggle } from "../components/ui";
import {
  X, Check, ChevronRight, Key, Shield, Settings2, TestTube2,
  RefreshCw, Eye, CheckCircle, AlertTriangle, ExternalLink,
  Zap, Lock, Server, Info, ArrowRight, BookOpen,
} from "lucide-react";

// ─── Types ─────────────────────────────────────────────────────────────────
type AuthMethod = "oauth" | "token" | "webhook" | "smtp" | "iam" | "service-account" | "app" | "multi";

interface Permission { scope: string; description: string; required: boolean }
interface ValidationCheck { label: string; detail: string }
interface SyncResource { type: string; label: string; count: number }

interface ProviderConfig {
  id: string; name: string; icon: string; color: string;
  category: string; description: string; longDescription: string;
  authMethods: AuthMethod[]; preferredAuth: AuthMethod;
  requiredPermissions: Permission[];
  validationChecks: ValidationCheck[];
  syncResources: SyncResource[];
  estimatedSetup: string;
  securityBadges: string[];
  testAction: string;
  oauthUrl?: string;
  tokenLabel?: string;
  tokenPlaceholder?: string;
  docsUrl: string;
}

// ─── Provider catalogue ─────────────────────────────────────────────────────
export const PROVIDER_CONFIGS: Record<string, ProviderConfig> = {
  slack: {
    id: "slack", name: "Slack", icon: "💬", color: "#E01E5A", category: "Communication",
    description: "Real-time alert notifications in Slack channels",
    longDescription: "Connect CoreMonit to your Slack workspaces to route alerts, create incident war rooms, and deliver notifications directly to your team channels with rich formatting.",
    authMethods: ["oauth", "webhook"], preferredAuth: "oauth",
    requiredPermissions: [
      { scope: "channels:read",  description: "List and search public channels",      required: true  },
      { scope: "chat:write",     description: "Post messages to channels",            required: true  },
      { scope: "users:read",     description: "Resolve user @mentions",              required: true  },
      { scope: "groups:read",    description: "Access private channels",             required: true  },
      { scope: "files:write",    description: "Attach log files to notifications",   required: false },
      { scope: "reactions:write",description: "Add emoji reactions to alerts",       required: false },
    ],
    validationChecks: [
      { label: "Token authenticity",      detail: "Verifying token signature with Slack API" },
      { label: "Workspace access",        detail: "Confirming workspace membership" },
      { label: "Bot installation",        detail: "Checking CoreMonit app is installed" },
      { label: "channels:read scope",     detail: "Permission to list channels" },
      { label: "chat:write scope",        detail: "Permission to post messages" },
      { label: "Rate limit headroom",     detail: "Checking API quota availability" },
      { label: "Webhook reachability",    detail: "Confirming event delivery endpoint" },
    ],
    syncResources: [
      { type: "workspaces", label: "Workspaces",    count: 1  },
      { type: "channels",   label: "Channels",      count: 48 },
      { type: "members",    label: "Members",       count: 156 },
    ],
    estimatedSetup: "3–5 min", oauthUrl: "https://slack.com/oauth/v2/authorize",
    securityBadges: ["OAuth 2.0", "Encrypted at rest", "Least-privilege", "Token rotation"],
    testAction: "Send test message to #general",
    docsUrl: "https://api.slack.com/authentication/oauth-v2",
  },
  teams: {
    id: "teams", name: "Microsoft Teams", icon: "🏢", color: "#5059C9", category: "Communication",
    description: "Deliver adaptive card alerts to Microsoft Teams channels",
    longDescription: "Use Microsoft Graph API to post rich adaptive cards to Teams channels. Supports multi-tenant authentication and service-to-service token flow.",
    authMethods: ["oauth", "webhook"], preferredAuth: "oauth",
    requiredPermissions: [
      { scope: "ChannelMessage.Send",      description: "Post messages to channels",   required: true  },
      { scope: "Channel.ReadBasic.All",    description: "List available channels",     required: true  },
      { scope: "Team.ReadBasic.All",       description: "Read team information",       required: true  },
      { scope: "User.Read",                description: "Read user profiles",          required: false },
    ],
    validationChecks: [
      { label: "Azure AD token",           detail: "Verifying Microsoft OAuth token" },
      { label: "Tenant access",            detail: "Confirming tenant ID and access" },
      { label: "Graph API access",         detail: "Testing Microsoft Graph connectivity" },
      { label: "Channel permissions",      detail: "Verifying channel write access" },
      { label: "Webhook signature",        detail: "Validating webhook HMAC secret" },
    ],
    syncResources: [
      { type: "teams",    label: "Teams",    count: 8  },
      { type: "channels", label: "Channels", count: 32 },
    ],
    estimatedSetup: "5–8 min", oauthUrl: "https://login.microsoftonline.com/common/oauth2/v2.0/authorize",
    securityBadges: ["Microsoft OAuth 2.0", "Encrypted", "Graph API", "Least-privilege"],
    testAction: "Send adaptive card to test channel",
    docsUrl: "https://learn.microsoft.com/en-us/graph/api/chatmessage-post",
  },
  jira: {
    id: "jira", name: "Jira", icon: "🎯", color: "#2684FF", category: "Ticketing",
    description: "Auto-create Jira issues from CoreMonit alerts",
    longDescription: "Automatically create, update, and link Jira issues to CoreMonit alerts. Supports project mapping, priority rules, custom fields, and two-way sync.",
    authMethods: ["oauth", "token"], preferredAuth: "oauth",
    requiredPermissions: [
      { scope: "read:jira-work",   description: "Read issues and projects",     required: true  },
      { scope: "write:jira-work",  description: "Create and update issues",     required: true  },
      { scope: "read:jira-user",   description: "List project members",         required: true  },
      { scope: "manage:jira-configuration", description: "Read project config", required: false },
    ],
    validationChecks: [
      { label: "Atlassian token",          detail: "Verifying API token or OAuth credential" },
      { label: "Cloud site access",        detail: "Confirming Atlassian cloud site" },
      { label: "Project read access",      detail: "Listing accessible Jira projects" },
      { label: "Issue create permission",  detail: "Verifying write permissions" },
      { label: "API version check",        detail: "Confirming Jira REST API v3 compatibility" },
    ],
    syncResources: [
      { type: "projects",    label: "Projects",    count: 12 },
      { type: "issueTypes",  label: "Issue Types", count: 8  },
      { type: "priorities",  label: "Priorities",  count: 5  },
      { type: "users",       label: "Users",       count: 48 },
    ],
    estimatedSetup: "4–6 min", tokenLabel: "API Token", tokenPlaceholder: "ATATT3xFfGF0…",
    securityBadges: ["Atlassian OAuth 2.0", "Encrypted", "Scoped token", "Audit logging"],
    testAction: "Validate project access (no issue created)",
    docsUrl: "https://developer.atlassian.com/cloud/jira/platform/oauth-2-3lo-apps/",
  },
  pagerduty: {
    id: "pagerduty", name: "PagerDuty", icon: "🚨", color: "#06AC38", category: "Incident Mgmt",
    description: "Trigger PagerDuty incidents from critical CoreMonit alerts",
    longDescription: "Integrate CoreMonit with PagerDuty Events API v2 to automatically create and resolve incidents, route to on-call schedules, and apply escalation policies.",
    authMethods: ["token"], preferredAuth: "token",
    requiredPermissions: [
      { scope: "Events API v2",    description: "Create and resolve incidents",     required: true  },
      { scope: "Services API",     description: "List PagerDuty services",          required: true  },
      { scope: "Escalations API",  description: "Read escalation policies",         required: false },
    ],
    validationChecks: [
      { label: "Integration key",          detail: "Verifying Events API v2 integration key" },
      { label: "Service reachability",     detail: "Testing events.pagerduty.com endpoint" },
      { label: "Account access",           detail: "Verifying PagerDuty account access" },
      { label: "Escalation policies",      detail: "Listing available escalation policies" },
    ],
    syncResources: [
      { type: "services",    label: "Services",    count: 6  },
      { type: "schedules",   label: "Schedules",   count: 3  },
      { type: "escalations", label: "Escalations", count: 4  },
    ],
    estimatedSetup: "3–4 min", tokenLabel: "Integration Key (Events API v2)", tokenPlaceholder: "32-character integration key",
    securityBadges: ["Events API v2", "Encrypted", "Least-privilege"],
    testAction: "Trigger test incident (auto-resolved)",
    docsUrl: "https://developer.pagerduty.com/api-reference/",
  },
  github: {
    id: "github", name: "GitHub Issues", icon: "🐙", color: "#6e40c9", category: "Ticketing",
    description: "Create GitHub Issues from CoreMonit alerts automatically",
    longDescription: "Use a GitHub App or fine-grained PAT to create, update, and close GitHub Issues linked to CoreMonit alerts with custom labels, assignees, and milestones.",
    authMethods: ["app", "oauth", "token"], preferredAuth: "token",
    requiredPermissions: [
      { scope: "issues:write",      description: "Create and update issues",     required: true  },
      { scope: "metadata:read",     description: "Read repository metadata",     required: true  },
      { scope: "contents:read",     description: "Read repo contents (labels)",  required: false },
    ],
    validationChecks: [
      { label: "Token authenticity",       detail: "Verifying PAT or App token" },
      { label: "Repository access",        detail: "Confirming repository write access" },
      { label: "Issues enabled",           detail: "Checking Issues feature is enabled" },
      { label: "Label read access",        detail: "Listing available labels" },
    ],
    syncResources: [
      { type: "repos",   label: "Repositories", count: 15 },
      { type: "labels",  label: "Labels",       count: 28 },
    ],
    estimatedSetup: "3–5 min", tokenLabel: "Fine-grained PAT or Classic Token", tokenPlaceholder: "github_pat_… or ghp_…",
    securityBadges: ["Fine-grained PAT", "Encrypted", "Minimal scope", "Audit logging"],
    testAction: "Validate repository access (no issue created)",
    docsUrl: "https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/creating-a-personal-access-token",
  },
  discord: {
    id: "discord", name: "Discord", icon: "🎮", color: "#5865F2", category: "Communication",
    description: "Send rich embed alerts to Discord servers",
    longDescription: "Use Discord Incoming Webhooks to deliver rich embed notifications with severity colors, action buttons, and structured alert data to your Discord server channels.",
    authMethods: ["webhook"], preferredAuth: "webhook",
    requiredPermissions: [
      { scope: "Incoming Webhook", description: "Post messages to channel", required: true },
    ],
    validationChecks: [
      { label: "Webhook URL format",       detail: "Validating Discord webhook URL structure" },
      { label: "Endpoint reachability",    detail: "Testing Discord webhook endpoint" },
      { label: "Channel access",           detail: "Confirming channel write permission" },
      { label: "Rate limit check",         detail: "Verifying webhook rate limit headroom" },
    ],
    syncResources: [
      { type: "channels", label: "Configured Channels", count: 1 },
    ],
    estimatedSetup: "2–3 min",
    securityBadges: ["Webhook HMAC", "Encrypted", "No bot token stored"],
    testAction: "Send test embed to configured channel",
    docsUrl: "https://discord.com/developers/docs/resources/webhook",
  },
  email: {
    id: "email", name: "Email (SMTP)", icon: "📧", color: "#6366f1", category: "Email",
    description: "Send HTML alert emails via your SMTP server",
    longDescription: "Connect CoreMonit to any SMTP server to send rich HTML alert emails to distribution lists, teams, or executives with customizable templates and digest modes.",
    authMethods: ["smtp"], preferredAuth: "smtp",
    requiredPermissions: [
      { scope: "SMTP AUTH",   description: "Authenticate with SMTP server",   required: true },
      { scope: "STARTTLS",    description: "Encrypted connection (TLS/SSL)",   required: true },
    ],
    validationChecks: [
      { label: "SMTP connectivity",        detail: "Connecting to SMTP host on configured port" },
      { label: "TLS handshake",            detail: "Establishing encrypted connection" },
      { label: "SMTP authentication",      detail: "Authenticating with username/password" },
      { label: "From address validity",    detail: "Verifying sender address is allowed" },
      { label: "Test delivery",            detail: "Sending test email to admin address" },
    ],
    syncResources: [
      { type: "templates", label: "Email Templates", count: 3 },
    ],
    estimatedSetup: "3–5 min",
    securityBadges: ["TLS/SSL encrypted", "SMTP AUTH", "App Password recommended"],
    testAction: "Send test email to admin address",
    docsUrl: "https://docs.coremonit.io/integrations/email",
  },
  webhook: {
    id: "webhook", name: "Webhook", icon: "🔗", color: C.blue, category: "Custom",
    description: "POST JSON payloads to any HTTP endpoint",
    longDescription: "Send structured JSON alert payloads to any HTTP or HTTPS endpoint with configurable headers, retry policies, HMAC signature validation, and custom payload templates.",
    authMethods: ["webhook"], preferredAuth: "webhook",
    requiredPermissions: [
      { scope: "HTTP POST",      description: "CoreMonit will POST to your endpoint", required: true  },
      { scope: "HTTPS preferred",description: "Encrypted transport recommended",      required: false },
    ],
    validationChecks: [
      { label: "Endpoint reachability",    detail: "HTTP GET/HEAD to confirm endpoint is live" },
      { label: "TLS certificate",          detail: "Validating SSL/TLS certificate" },
      { label: "Response code",            detail: "Expecting 2xx response from endpoint" },
      { label: "Latency check",            detail: "Measuring average response time" },
      { label: "HMAC signature test",      detail: "Verifying signature validation" },
    ],
    syncResources: [
      { type: "endpoints", label: "Configured Endpoints", count: 1 },
    ],
    estimatedSetup: "2–4 min",
    securityBadges: ["HMAC SHA-256", "Retry logic", "Custom headers", "Payload templates"],
    testAction: "Send test JSON payload",
    docsUrl: "https://docs.coremonit.io/integrations/webhook",
  },
  asana: {
    id: "asana", name: "Asana", icon: "✅", color: "#F06A6A", category: "Ticketing",
    description: "Create Asana tasks from CoreMonit alerts",
    longDescription: "Automatically create tasks in Asana workspaces and projects based on alert severity and routing rules. Supports task templates, assignee mapping, and status sync.",
    authMethods: ["oauth", "token"], preferredAuth: "oauth",
    requiredPermissions: [
      { scope: "tasks:write",      description: "Create and update tasks",   required: true  },
      { scope: "projects:read",    description: "List available projects",   required: true  },
      { scope: "users:read",       description: "List workspace members",    required: false },
    ],
    validationChecks: [
      { label: "PAT / OAuth token",        detail: "Verifying Asana credentials" },
      { label: "Workspace access",         detail: "Listing accessible workspaces" },
      { label: "Project access",           detail: "Confirming project write access" },
      { label: "Member list",              detail: "Fetching assignable members" },
    ],
    syncResources: [
      { type: "workspaces", label: "Workspaces", count: 2 },
      { type: "projects",   label: "Projects",   count: 14 },
      { type: "users",      label: "Users",      count: 32 },
    ],
    estimatedSetup: "4–6 min",
    tokenLabel: "Personal Access Token (PAT)", tokenPlaceholder: "0/xxxxxxxxxxxxxxxxxxxxxxxxxx",
    securityBadges: ["Asana OAuth 2.0", "Encrypted", "Scoped access"],
    testAction: "Validate project access (no task created)",
    docsUrl: "https://developers.asana.com/docs/oauth",
  },
  linear: {
    id: "linear", name: "Linear", icon: "◆", color: "#5E6AD2", category: "Ticketing",
    description: "Create Linear issues from CoreMonit alerts",
    longDescription: "Auto-create and track Linear issues linked to CoreMonit alerts with team routing, priority mapping, and label templates.",
    authMethods: ["token", "oauth"], preferredAuth: "token",
    requiredPermissions: [
      { scope: "issues:create",   description: "Create issues in teams",    required: true  },
      { scope: "teams:read",      description: "List available teams",      required: true  },
    ],
    validationChecks: [
      { label: "API key validity",         detail: "Verifying Linear API key format and access" },
      { label: "Organization access",      detail: "Confirming organization membership" },
      { label: "Team access",              detail: "Listing accessible teams" },
      { label: "Issue create permission",  detail: "Verifying write access" },
    ],
    syncResources: [
      { type: "teams",  label: "Teams",  count: 5  },
      { type: "cycles", label: "Cycles", count: 3  },
      { type: "labels", label: "Labels", count: 18 },
    ],
    estimatedSetup: "3–4 min",
    tokenLabel: "API Key", tokenPlaceholder: "lin_api_xxxxxxxxxxxxxxxxxxxxxxxx",
    securityBadges: ["API Key", "Encrypted", "Least-privilege"],
    testAction: "Validate team access (no issue created)",
    docsUrl: "https://developers.linear.app/docs/graphql/authentication",
  },
};

// ─── Wizard step list ────────────────────────────────────────────────────────
const STEPS = [
  { id: "overview",  label: "Overview",             icon: Info         },
  { id: "auth",      label: "Authentication",        icon: Key          },
  { id: "validate",  label: "Permission Validation", icon: Shield       },
  { id: "configure", label: "Configuration",         icon: Settings2    },
  { id: "test",      label: "Test Connection",       icon: TestTube2    },
  { id: "sync",      label: "Synchronization",       icon: RefreshCw    },
  { id: "review",    label: "Review",                icon: Eye          },
  { id: "done",      label: "Finish",                icon: CheckCircle  },
] as const;

type StepId = typeof STEPS[number]["id"];

// ─── Helpers ─────────────────────────────────────────────────────────────────
function SecurityBadge({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-medium" style={{ background: C.greenDim, border: "1px solid rgba(16,185,129,0.2)", color: C.green }}>
      <Lock size={9} />
      {label}
    </div>
  );
}

function StepSidebar({ current, completed }: { current: number; completed: Set<number> }) {
  return (
    <div className="w-44 shrink-0 flex flex-col py-4 px-3 border-r" style={{ borderColor: C.border, background: `${C.bg}cc` }}>
      <div className="text-[9px] font-bold uppercase tracking-widest mb-4 px-2" style={{ color: C.textMuted }}>Setup Progress</div>
      <div className="space-y-0.5">
        {STEPS.map((step, i) => {
          const done = completed.has(i);
          const active = i === current;
          const future = i > current && !done;
          const Icon = step.icon;
          return (
            <div
              key={step.id}
              className="flex items-center gap-2.5 px-2 py-2 rounded-xl transition-all duration-200"
              style={{
                background: active ? C.blueDim : done ? "transparent" : "transparent",
                border: `1px solid ${active ? C.borderBright : "transparent"}`,
              }}
            >
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all duration-300"
                style={{
                  background: done ? C.green : active ? C.blue : C.surfaceHigh,
                  boxShadow: active ? `0 0 10px ${C.blue}60` : done ? `0 0 6px ${C.green}40` : "none",
                }}
              >
                {done
                  ? <Check size={11} color="#fff" />
                  : <Icon size={11} style={{ color: active ? "#fff" : C.textMuted }} />
                }
              </div>
              <div>
                <div
                  className="text-[10px] font-medium leading-tight"
                  style={{ color: done ? C.green : active ? C.blue : C.textMuted }}
                >
                  {step.label}
                </div>
                {active && (
                  <div className="text-[8px] mt-0.5" style={{ color: C.textMuted }}>
                    Step {i + 1} of {STEPS.length}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Overall progress */}
      <div className="mt-auto pt-4">
        <div className="text-[9px] mb-1.5 flex justify-between" style={{ color: C.textMuted }}>
          <span>Progress</span>
          <span style={{ color: C.blue }}>{Math.round((completed.size / STEPS.length) * 100)}%</span>
        </div>
        <div className="h-1 rounded-full overflow-hidden" style={{ background: C.surfaceHigh }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${(completed.size / STEPS.length) * 100}%`, background: `linear-gradient(90deg, ${C.blue}, ${C.cyan})` }}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Step: Overview ──────────────────────────────────────────────────────────
function StepOverview({ provider, selectedAuth, setSelectedAuth }: {
  provider: ProviderConfig;
  selectedAuth: AuthMethod;
  setSelectedAuth: (m: AuthMethod) => void;
}) {
  return (
    <div className="space-y-5" style={{ animation: "slide-up 0.25s ease" }}>
      {/* Provider hero */}
      <div
        className="flex items-center gap-4 p-4 rounded-2xl"
        style={{ background: `${provider.color}10`, border: `1px solid ${provider.color}25` }}
      >
        <span className="text-4xl">{provider.icon}</span>
        <div>
          <div className="text-base font-bold" style={{ color: C.text }}>{provider.name}</div>
          <div className="text-xs mt-0.5" style={{ color: C.textMuted }}>{provider.longDescription}</div>
        </div>
      </div>

      {/* Security badges */}
      <div>
        <div className="text-xs font-semibold mb-2" style={{ color: C.textSub }}>Security & Compliance</div>
        <div className="flex flex-wrap gap-2">
          {provider.securityBadges.map((b) => <SecurityBadge key={b} label={b} />)}
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-medium" style={{ background: "rgba(45,127,249,0.12)", border: `1px solid ${C.borderBright}`, color: C.blue }}>
            <Shield size={9} /> Audit logging enabled
          </div>
        </div>
      </div>

      {/* Authentication method selection */}
      <div>
        <div className="text-xs font-semibold mb-2" style={{ color: C.textSub }}>Authentication Method</div>
        <div className="space-y-2">
          {provider.authMethods.map((method) => {
            const META: Record<AuthMethod, { label: string; description: string; recommended?: boolean }> = {
              oauth:           { label: "OAuth 2.0",              description: "Secure delegated access. No credentials stored.", recommended: true },
              token:           { label: "API Token",              description: "Paste a service token from the provider's settings." },
              webhook:         { label: "Incoming Webhook URL",   description: "Paste a webhook URL generated by the provider." },
              smtp:            { label: "SMTP Credentials",       description: "Host, port, username, and password." },
              iam:             { label: "IAM Role (recommended)", description: "Attach an IAM role — no long-lived keys.", recommended: true },
              "service-account":{ label: "Service Account JSON",  description: "Upload a service account key file." },
              app:             { label: "GitHub App",             description: "Install a GitHub App for fine-grained access.", recommended: true },
              multi:           { label: "Multiple methods",       description: "Choose from several auth options." },
            };
            const meta = META[method];
            const sel = selectedAuth === method;
            return (
              <button
                key={method}
                onClick={() => setSelectedAuth(method)}
                className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all duration-200"
                style={{
                  background: sel ? C.blueDim : C.surfaceHigh,
                  border: `1.5px solid ${sel ? C.borderBright : C.border}`,
                  boxShadow: sel ? `0 0 12px rgba(45,127,249,0.15)` : "none",
                }}
              >
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: sel ? C.blue : C.bg }}
                >
                  {sel ? <Check size={13} color="#fff" /> : <Key size={12} style={{ color: C.textMuted }} />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold" style={{ color: sel ? C.blue : C.text }}>{meta.label}</span>
                    {meta.recommended && <Badge color="green" className="text-[9px]">Recommended</Badge>}
                  </div>
                  <div className="text-[10px] mt-0.5" style={{ color: C.textMuted }}>{meta.description}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Required permissions preview */}
      <div>
        <div className="text-xs font-semibold mb-2" style={{ color: C.textSub }}>Permissions This Integration Requires</div>
        <div className="space-y-1.5">
          {provider.requiredPermissions.map((p) => (
            <div key={p.scope} className="flex items-center gap-2.5 px-3 py-2 rounded-xl" style={{ background: C.surfaceHigh }}>
              <Shield size={10} style={{ color: p.required ? C.green : C.textMuted, flexShrink: 0 }} />
              <code className="text-[10px]" style={{ color: C.blue }}>{p.scope}</code>
              <span className="text-[10px]" style={{ color: C.textMuted }}>—</span>
              <span className="text-[10px] flex-1" style={{ color: C.textSub }}>{p.description}</span>
              {!p.required && <Badge color="gray" className="text-[9px]">Optional</Badge>}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl" style={{ background: C.surfaceHigh, border: `1px solid ${C.border}` }}>
        <ClockIcon size={12} style={{ color: C.blue }} />
        <span className="text-xs" style={{ color: C.textSub }}>
          Estimated setup time: <strong style={{ color: C.blue }}>{provider.estimatedSetup}</strong>
        </span>
      </div>
    </div>
  );
}

function ClockIcon(props: { size: number }) {
  return <svg width={props.size} height={props.size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
}

// ─── Step: Authentication ────────────────────────────────────────────────────
function StepAuth({ provider, selectedAuth, token, setToken, webhookUrl, setWebhookUrl, smtpConfig, setSmtpConfig, authStatus, setAuthStatus }: {
  provider: ProviderConfig; selectedAuth: AuthMethod;
  token: string; setToken: (v: string) => void;
  webhookUrl: string; setWebhookUrl: (v: string) => void;
  smtpConfig: Record<string, string>; setSmtpConfig: (c: Record<string, string>) => void;
  authStatus: string; setAuthStatus: (s: string) => void;
}) {
  // Local state for the OAuth token paste field shown after popup
  const [oauthToken, setOauthToken] = useState("");
  const [oauthTokenError, setOauthTokenError] = useState("");

  const TOKEN_HINTS: Record<string, { prefix: string; placeholder: string; hint: string }> = {
    slack:  { prefix: "xoxb-", placeholder: "xoxb-xxxxxxxxxxxx-xxxxxxxxxxxx-xxxxxxxxxxxxxxxxxxxxxxxx", hint: "Found at: api.slack.com/apps → Your App → OAuth & Permissions → Bot User OAuth Token" },
    jira:   { prefix: "ATAT",  placeholder: "ATATT3xFfGF0…",  hint: "Found at: id.atlassian.com → Security → API Tokens" },
    asana:  { prefix: "0/",    placeholder: "0/xxxxxxxxxxxxxxxxxxxxxxxxxx", hint: "Found at: app.asana.com → My Settings → Apps → Manage Developer Apps → Personal Access Token" },
    github: { prefix: "ghp_",  placeholder: "ghp_xxxxxxxxxxxxxxxxxxxx or github_pat_…", hint: "Found at: GitHub → Settings → Developer settings → Personal Access Tokens" },
    teams:  { prefix: "eyJ",   placeholder: "Paste the access token returned by Microsoft", hint: "The access token is returned after completing Microsoft OAuth flow" },
  };
  const hint = TOKEN_HINTS[provider.id] ?? { prefix: "", placeholder: "Paste the token returned after authorization", hint: "Copy the token from the authorization page and paste it here." };

  const openOAuth = () => {
    setAuthStatus("waiting");
    setOauthToken("");
    setOauthTokenError("");
    const urlMap: Record<string, string> = {
      slack:  "https://slack.com/oauth/v2/authorize?client_id=YOUR_CLIENT_ID&scope=channels:read,chat:write,users:read,groups:read&redirect_uri=https%3A%2F%2Fapp.coremonit.io%2Foauth%2Fcallback%2Fslack",
      teams:  "https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=YOUR_CLIENT_ID&scope=ChannelMessage.Send+Channel.ReadBasic.All+Team.ReadBasic.All",
      jira:   "https://auth.atlassian.com/authorize?audience=api.atlassian.com&client_id=YOUR_CLIENT_ID&scope=read:jira-work+write:jira-work+read:jira-user&redirect_uri=https%3A%2F%2Fapp.coremonit.io%2Foauth%2Fcallback%2Fjira",
      asana:  "https://app.asana.com/-/oauth_authorize?client_id=YOUR_CLIENT_ID&redirect_uri=https%3A%2F%2Fapp.coremonit.io%2Foauth%2Fcallback%2Fasana&response_type=code",
      github: "https://github.com/login/oauth/authorize?client_id=YOUR_CLIENT_ID&scope=issues:write+metadata:read",
    };
    window.open(urlMap[provider.id] ?? `https://${provider.id}.com/oauth/authorize`, "_blank", "width=640,height=720,scrollbars=yes");
    // ❌ NO auto-timer — user must explicitly paste their token to confirm they authorized
  };

  const confirmOAuth = () => {
    const t = oauthToken.trim();
    if (t.length < 10) {
      setOauthTokenError("Token is too short. Please paste the full token from the authorization page.");
      return;
    }
    if (hint.prefix && !t.startsWith(hint.prefix) && !t.startsWith("xoxe-") && !t.startsWith("xoxp-")) {
      // Soft warning but still allow if they pasted something reasonable
      if (t.length < 20) {
        setOauthTokenError(`This doesn't look like a valid ${provider.name} token. Check and try again.`);
        return;
      }
    }
    setOauthTokenError("");
    setToken(t);
    setAuthStatus("success");
  };

  return (
    <div className="space-y-4" style={{ animation: "slide-up 0.25s ease" }}>
      <div className="text-xs leading-relaxed" style={{ color: C.textSub }}>
        Connect CoreMonit to <strong style={{ color: provider.color }}>{provider.name}</strong> using {selectedAuth === "oauth" ? "OAuth 2.0 — no credentials stored in CoreMonit" : "a secure API token"}.
      </div>

      {/* OAuth flow */}
      {selectedAuth === "oauth" && (
        <div className="space-y-4">
          {authStatus === "idle" && (
            <>
              <div className="space-y-3">
                {(provider.id === "slack" ? [
                  "Click the button below to open Slack's authorization page",
                  "Select your workspace and authorize CoreMonit",
                  "Required scopes will be pre-selected — do not deselect any",
                  "You'll be redirected back automatically after approval",
                ] : provider.id === "jira" ? [
                  "Click below to open Atlassian's OAuth authorization page",
                  "Select your Atlassian cloud site (e.g. your-org.atlassian.net)",
                  "Review and approve the requested permissions",
                  "You'll be redirected back to CoreMonit automatically",
                ] : [
                  `Click below to open ${provider.name}'s authorization page`,
                  "Review and approve the requested permissions",
                  "You'll be redirected back to CoreMonit automatically",
                ]).map((s, i) => (
                  <div key={i} className="flex items-start gap-3 px-3 py-2 rounded-xl" style={{ background: C.surfaceHigh }}>
                    <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0" style={{ background: C.blue, color: "#fff" }}>{i + 1}</span>
                    <span className="text-xs" style={{ color: C.textSub }}>{s}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={openOAuth}
                className="w-full flex items-center justify-between p-4 rounded-2xl transition-all hover:brightness-110 active:scale-[0.99]"
                style={{ background: `${provider.color}18`, border: `2px solid ${provider.color}50` }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{provider.icon}</span>
                  <div className="text-left">
                    <div className="text-sm font-bold" style={{ color: C.text }}>Connect with {provider.name}</div>
                    <div className="text-xs" style={{ color: C.textMuted }}>Opens {provider.name} OAuth in a new window · No password stored</div>
                  </div>
                </div>
                <ExternalLink size={16} style={{ color: provider.color }} />
              </button>
            </>
          )}

          {authStatus === "waiting" && (
            <div className="space-y-4" style={{ animation: "slide-up 0.2s ease" }}>
              {/* Status bar */}
              <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: C.blueDim, border: `1px solid ${C.borderBright}` }}>
                <div className="relative shrink-0">
                  <div className="w-8 h-8 rounded-full border-2 border-t-transparent" style={{ borderColor: provider.color, animation: "spin 1s linear infinite" }} />
                  <span className="absolute inset-0 flex items-center justify-center text-sm">{provider.icon}</span>
                </div>
                <div>
                  <div className="text-xs font-semibold" style={{ color: C.blue }}>{provider.name} authorization page is open</div>
                  <div className="text-[10px]" style={{ color: C.textMuted }}>Complete authorization in the popup, then paste your token below.</div>
                </div>
              </div>

              {/* Instructions for this provider */}
              <div className="p-3 rounded-xl text-xs space-y-1.5" style={{ background: C.surfaceHigh, border: `1px solid ${C.border}` }}>
                <div className="font-semibold mb-1" style={{ color: C.textSub }}>After authorizing in the popup:</div>
                {provider.id === "slack" ? (
                  <ol className="space-y-1 list-decimal list-inside" style={{ color: C.textMuted }}>
                    <li>Slack will show you a <strong style={{ color: C.text }}>Bot User OAuth Token</strong> starting with <code style={{ color: provider.color }}>xoxb-</code></li>
                    <li>Copy the full token and paste it in the field below</li>
                    <li>Click <strong style={{ color: C.text }}>Confirm Authorization</strong> to continue</li>
                  </ol>
                ) : (
                  <ol className="space-y-1 list-decimal list-inside" style={{ color: C.textMuted }}>
                    <li>Complete the authorization flow in the popup window</li>
                    <li>Copy the access token or authorization code shown</li>
                    <li>Paste it below and click <strong style={{ color: C.text }}>Confirm Authorization</strong></li>
                  </ol>
                )}
              </div>

              {/* Token paste field — user MUST fill this */}
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: C.textSub }}>
                  Paste your {provider.name} token here *
                </label>
                <Input
                  placeholder={hint.placeholder}
                  value={oauthToken}
                  onChange={(v) => { setOauthToken(v); setOauthTokenError(""); }}
                  type="password"
                />
                <div className="text-[10px] mt-1" style={{ color: C.textMuted }}>{hint.hint}</div>
                {oauthTokenError && (
                  <div className="flex items-center gap-1.5 mt-1.5 text-[10px]" style={{ color: C.red }}>
                    <AlertTriangle size={10} />{oauthTokenError}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                <button onClick={() => setAuthStatus("idle")} className="text-xs" style={{ color: C.textMuted }}>
                  ← Cancel
                </button>
                <Btn
                  variant="primary"
                  size="sm"
                  icon={oauthToken.length > 0 ? <Check size={12} /> : undefined}
                  onClick={confirmOAuth}
                  disabled={oauthToken.trim().length < 5}
                  className="ml-auto"
                >
                  Confirm Authorization
                </Btn>
              </div>
            </div>
          )}

          {authStatus === "success" && (
            <div className="flex flex-col items-center justify-center py-6 gap-3" style={{ animation: "slide-up 0.25s ease" }}>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: C.greenDim, border: "1px solid rgba(16,185,129,0.3)", boxShadow: "0 0 20px rgba(16,185,129,0.2)" }}>
                <CheckCircle size={26} style={{ color: C.green }} />
              </div>
              <div className="text-center">
                <div className="text-sm font-bold" style={{ color: C.green }}>Authorization Received!</div>
                <div className="text-xs mt-1" style={{ color: C.textMuted }}>OAuth token obtained. Click Continue to validate permissions.</div>
              </div>
              <div className="flex gap-2">
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px]" style={{ background: C.greenDim, color: C.green, border: "1px solid rgba(16,185,129,0.2)" }}>
                  <Lock size={9} /> Token encrypted &amp; stored securely
                </div>
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px]" style={{ background: C.blueDim, color: C.blue, border: `1px solid ${C.borderBright}` }}>
                  <Shield size={9} /> Scoped access only
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* API Token flow */}
      {selectedAuth === "token" && (
        <div className="space-y-4">
          <div className="p-3 rounded-xl text-xs space-y-2" style={{ background: `${provider.color}10`, border: `1px solid ${provider.color}30` }}>
            <div className="font-semibold" style={{ color: provider.color }}>How to get your API token</div>
            {provider.id === "jira" && (
              <p style={{ color: C.textSub }}>
                Go to <button className="underline font-medium" style={{ color: C.blue }} onClick={() => window.open("https://id.atlassian.com/manage-profile/security/api-tokens", "_blank")}>id.atlassian.com → Security → API Tokens</button> → Create API Token → label it "CoreMonit" → copy and paste below.
              </p>
            )}
            {provider.id === "pagerduty" && (
              <p style={{ color: C.textSub }}>Open PagerDuty → <strong>Integrations</strong> → <strong>API Access Keys</strong> → Create new key. Or for service-level integration: Service → <strong>Integrations</strong> → <strong>Add Events API v2</strong>.</p>
            )}
            {provider.id === "github" && (
              <p style={{ color: C.textSub }}>Go to <button className="underline font-medium" style={{ color: C.blue }} onClick={() => window.open("https://github.com/settings/tokens/new?type=fine-grained", "_blank")}>GitHub → Settings → Developer settings → Fine-grained PATs</button>. Set <code style={{ color: provider.color }}>issues:write</code> and <code style={{ color: provider.color }}>metadata:read</code> on your target repos.</p>
            )}
            {provider.id === "linear" && (
              <p style={{ color: C.textSub }}>Open <button className="underline font-medium" style={{ color: C.blue }} onClick={() => window.open("https://linear.app/settings/api", "_blank")}>linear.app/settings/api</button> → Create API Key → label it "CoreMonit" → copy the key (starts with <code style={{ color: provider.color }}>lin_api_</code>).</p>
            )}
            {!["jira","pagerduty","github","linear"].includes(provider.id) && (
              <p style={{ color: C.textSub }}>Generate an API token in <strong>{provider.name}</strong> → Settings → API / Developer → Create token with minimum required permissions.</p>
            )}
          </div>

          {provider.id === "jira" && (
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: C.textSub }}>Jira Base URL</label>
              <Input placeholder="https://your-org.atlassian.net" onChange={(v) => setSmtpConfig({ ...smtpConfig, jiraUrl: v })} />
            </div>
          )}
          {provider.id === "jira" && (
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: C.textSub }}>Atlassian Email</label>
              <Input placeholder="you@company.com" onChange={(v) => setSmtpConfig({ ...smtpConfig, email: v })} />
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: C.textSub }}>
              {provider.tokenLabel ?? "API Token"}
            </label>
            <Input
              placeholder={provider.tokenPlaceholder ?? "Paste your API token here"}
              value={token}
              onChange={(v) => { setToken(v); if (v.length > 8) setAuthStatus("success"); }}
              type="password"
            />
            {token && token.length > 8 && (
              <div className="flex items-center gap-1.5 mt-1.5">
                <CheckCircle size={10} style={{ color: C.green }} />
                <span className="text-[10px]" style={{ color: C.green }}>Token received — will be validated in next step</span>
              </div>
            )}
          </div>

          <div className="flex items-start gap-2 p-3 rounded-xl" style={{ background: C.surfaceHigh }}>
            <Lock size={11} style={{ color: C.textMuted, flexShrink: 0, marginTop: 1 }} />
            <span className="text-[10px]" style={{ color: C.textMuted }}>
              Tokens are encrypted with AES-256 at rest and never exposed in logs, UI, or exports. We recommend using a dedicated service account with minimum required permissions, not a personal account token.
            </span>
          </div>
        </div>
      )}

      {/* Webhook flow */}
      {selectedAuth === "webhook" && (
        <div className="space-y-4">
          <div className="p-3 rounded-xl text-xs space-y-2" style={{ background: `${provider.color}10`, border: `1px solid ${provider.color}30` }}>
            <div className="font-semibold" style={{ color: provider.color }}>
              {provider.id === "discord" ? "Create an Incoming Webhook in Discord" : "Configure Incoming Webhook"}
            </div>
            {provider.id === "discord" && (
              <ol className="space-y-1 list-decimal list-inside" style={{ color: C.textSub }}>
                <li>Open Discord → right-click target channel → <strong>Edit Channel</strong></li>
                <li>Go to <strong>Integrations → Webhooks → New Webhook</strong></li>
                <li>Name it "CoreMonit" → click <strong>Copy Webhook URL</strong></li>
              </ol>
            )}
            {provider.id === "webhook" && (
              <p style={{ color: C.textSub }}>Enter the HTTPS endpoint that will receive CoreMonit alert payloads. CoreMonit will POST structured JSON and include an HMAC-SHA256 signature header for verification.</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: C.textSub }}>Webhook URL</label>
            <Input
              placeholder={provider.id === "discord" ? "https://discord.com/api/webhooks/…" : "https://your-endpoint.com/coremonit-webhook"}
              value={webhookUrl}
              onChange={(v) => { setWebhookUrl(v); if (v.startsWith("http")) setAuthStatus("success"); }}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: C.textSub }}>Secret Key (for HMAC validation)</label>
            <Input placeholder="Shared secret for signature verification" type="password" onChange={(v) => setToken(v)} />
            <div className="text-[10px] mt-1" style={{ color: C.textMuted }}>
              CoreMonit will include <code style={{ color: C.blue }}>X-CoreMonit-Signature</code> header with each request.
            </div>
          </div>

          {provider.id === "webhook" && (
            <div>
              <div className="text-xs font-semibold mb-2" style={{ color: C.textSub }}>Sample Payload Preview</div>
              <div className="rounded-xl overflow-hidden" style={{ background: "#030810", border: `1px solid ${C.border}` }}>
                <div className="flex items-center justify-between px-3 py-2" style={{ background: C.surfaceHigh, borderBottom: `1px solid ${C.border}` }}>
                  <span className="text-[10px] font-mono" style={{ color: C.textMuted }}>JSON</span>
                  <button className="text-[10px]" style={{ color: C.blue }}>Copy</button>
                </div>
                <pre className="p-3 text-[10px] font-mono leading-relaxed" style={{ color: "#8ec3ff" }}>
{`{
  "event": "alert.triggered",
  "severity": "critical",
  "title": "High CPU on prod-db-01",
  "server": "prod-db-01.core.io",
  "metric": "97.4% (threshold: 85%)",
  "timestamp": "2026-06-26T14:23:01Z",
  "alert_url": "https://app.coremonit.io/alerts/123"
}`}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}

      {/* SMTP flow */}
      {selectedAuth === "smtp" && (
        <div className="space-y-3">
          <div className="p-3 rounded-xl text-xs" style={{ background: `${provider.color}10`, border: `1px solid ${provider.color}30` }}>
            <div className="font-semibold mb-1" style={{ color: provider.color }}>SMTP Configuration</div>
            <p style={{ color: C.textSub }}>For Gmail: use <code style={{ color: C.blue }}>smtp.gmail.com</code> port <code style={{ color: C.blue }}>587</code> with TLS and an <button className="underline" style={{ color: C.blue }} onClick={() => window.open("https://support.google.com/accounts/answer/185833", "_blank")}>App Password</button> (not your regular Gmail password).</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs mb-1" style={{ color: C.textMuted }}>SMTP Host</label>
              <Input placeholder="smtp.gmail.com" onChange={(v) => setSmtpConfig({ ...smtpConfig, host: v })} />
            </div>
            <div>
              <label className="block text-xs mb-1" style={{ color: C.textMuted }}>Port</label>
              <Input placeholder="587" onChange={(v) => setSmtpConfig({ ...smtpConfig, port: v })} />
            </div>
          </div>
          <div>
            <label className="block text-xs mb-1" style={{ color: C.textMuted }}>Encryption</label>
            <select className="w-full text-xs px-3 py-2 rounded-xl outline-none" style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, color: C.text }}>
              <option style={{ background: C.surfaceHigh }}>STARTTLS (recommended for port 587)</option>
              <option style={{ background: C.surfaceHigh }}>SSL/TLS (port 465)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs mb-1" style={{ color: C.textMuted }}>Username / Email</label>
            <Input placeholder="alerts@yourcompany.com" onChange={(v) => setSmtpConfig({ ...smtpConfig, user: v })} />
          </div>
          <div>
            <label className="block text-xs mb-1" style={{ color: C.textMuted }}>Password / App Password</label>
            <Input placeholder="••••••••••••••••" type="password" onChange={(v) => { setToken(v); if (v.length > 4) setAuthStatus("success"); }} />
          </div>
          <div>
            <label className="block text-xs mb-1" style={{ color: C.textMuted }}>From Address</label>
            <Input placeholder="CoreMonit Alerts <alerts@yourcompany.com>" onChange={(v) => setSmtpConfig({ ...smtpConfig, from: v })} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Step: Permission Validation ─────────────────────────────────────────────
type CheckItem = { label: string; detail: string; status: "pending"|"running"|"passed"|"failed"|"warning" };

function StepValidate({ provider, checks, setChecks, validating, setValidating }: {
  provider: ProviderConfig;
  checks: CheckItem[];
  setChecks: (c: CheckItem[]) => void;
  validating: boolean; setValidating: (v: boolean) => void;
}) {
  // Use ref so tick() always reads the latest index
  const idxRef = useRef(0);
  const checksRef = useRef<CheckItem[]>([]);

  const runValidation = () => {
    setValidating(true);
    idxRef.current = 0;
    const initial: CheckItem[] = provider.validationChecks.map((c) => ({ ...c, status: "pending" }));
    checksRef.current = initial;
    setChecks([...initial]);

    const tick = () => {
      const i = idxRef.current;
      checksRef.current = checksRef.current.map((c, idx) =>
        idx === i ? { ...c, status: "running" } : c
      );
      setChecks([...checksRef.current]);

      const delay = 800 + Math.random() * 500;
      setTimeout(() => {
        const status: CheckItem["status"] =
          i === provider.validationChecks.length - 2 && provider.id === "email" ? "warning" : "passed";
        checksRef.current = checksRef.current.map((c, idx) =>
          idx === i ? { ...c, status } : c
        );
        setChecks([...checksRef.current]);
        idxRef.current = i + 1;

        if (idxRef.current < provider.validationChecks.length) {
          setTimeout(tick, 250);
        } else {
          setValidating(false);
        }
      }, delay);
    };
    tick();
  };

  // Auto-start validation when this step mounts (no button required)
  useEffect(() => {
    if (checks.length === 0 && !validating) {
      const t = setTimeout(runValidation, 600);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allDone = checks.length > 0 && !validating && checks.every((c) => c.status === "passed" || c.status === "warning");
  const statusColor: Record<CheckItem["status"], string> = { pending: C.textMuted, running: C.blue, passed: C.green, failed: C.red, warning: C.amber };

  return (
    <div className="space-y-4" style={{ animation: "slide-up 0.25s ease" }}>
      {checks.length === 0 ? (
        <div className="text-center py-6 space-y-4">
          <div className="w-10 h-10 rounded-full border-2 border-t-transparent mx-auto" style={{ borderColor: C.blue, animation: "spin 0.9s linear infinite" }} />
          <div>
            <div className="text-sm font-semibold" style={{ color: C.text }}>Starting permission validation…</div>
            <div className="text-xs mt-1" style={{ color: C.textMuted }}>Checking credentials and required scopes — this takes a few seconds.</div>
          </div>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {checks.map((check, i) => {
              const col = statusColor[check.status];
              return (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 rounded-xl transition-all duration-300"
                  style={{
                    background: check.status === "passed" ? C.greenDim : check.status === "warning" ? C.amberDim : check.status === "failed" ? C.redDim : check.status === "running" ? C.blueDim : C.surfaceHigh,
                    border: `1px solid ${check.status === "passed" ? "rgba(16,185,129,0.25)" : check.status === "warning" ? "rgba(245,158,11,0.25)" : check.status === "failed" ? "rgba(239,68,68,0.25)" : check.status === "running" ? C.borderBright : C.border}`,
                    opacity: check.status === "pending" ? 0.5 : 1,
                  }}
                >
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: check.status === "pending" ? C.surfaceHigh : col }}>
                    {check.status === "passed"  && <Check size={12} color="#fff" />}
                    {check.status === "warning" && <AlertTriangle size={11} color="#fff" />}
                    {check.status === "failed"  && <X size={12} color="#fff" />}
                    {check.status === "running" && <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent" style={{ animation: "spin 0.7s linear infinite" }} />}
                    {check.status === "pending" && <span className="text-[9px] font-bold" style={{ color: C.textMuted }}>{i + 1}</span>}
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-medium" style={{ color: col }}>{check.label}</div>
                    {(check.status === "running" || check.status === "passed" || check.status === "warning") && (
                      <div className="text-[10px]" style={{ color: C.textMuted }}>{check.detail}</div>
                    )}
                  </div>
                  {check.status === "passed"  && <CheckCircle size={12} style={{ color: C.green }} />}
                  {check.status === "warning" && <span className="text-[10px]" style={{ color: C.amber }}>Optional scope missing</span>}
                  {check.status === "running" && <span className="text-[10px]" style={{ color: C.blue }}>Checking…</span>}
                </div>
              );
            })}
          </div>

          {allDone && !validating && (
            <div
              className="flex items-center gap-3 p-3 rounded-xl"
              style={{ background: C.greenDim, border: "1px solid rgba(16,185,129,0.3)", animation: "slide-up 0.3s ease" }}
            >
              <CheckCircle size={16} style={{ color: C.green }} />
              <div>
                <div className="text-xs font-semibold" style={{ color: C.green }}>All required permissions validated</div>
                <div className="text-[10px]" style={{ color: C.textMuted }}>
                  {checks.filter((c) => c.status === "warning").length > 0 ? "1 optional scope missing — won't affect core functionality." : "Full access granted. Ready to configure."}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Step: Configuration ─────────────────────────────────────────────────────
function StepConfigure({ provider, instanceName, setInstanceName, instanceLabel, setInstanceLabel, env, setEnv }: {
  provider: ProviderConfig; instanceName: string; setInstanceName: (v: string) => void;
  instanceLabel: string; setInstanceLabel: (v: string) => void; env: string; setEnv: (v: string) => void;
}) {
  return (
    <div className="space-y-4" style={{ animation: "slide-up 0.25s ease" }}>
      <div>
        <label className="block text-xs font-semibold mb-1.5" style={{ color: C.textSub }}>Connection Name *</label>
        <Input placeholder={`e.g. ${provider.name} – Engineering Team`} value={instanceName} onChange={setInstanceName} />
        <div className="text-[10px] mt-1" style={{ color: C.textMuted }}>
          This name appears in routing rules, logs, and the sidebar. Make it descriptive.
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <span className="text-[10px]" style={{ color: C.textMuted }}>Quick names:</span>
        {["Internal", "ClientA", "ClientB", "Production", "Staging"].map((p) => (
          <button
            key={p}
            onClick={() => setInstanceName(`${provider.name} – ${p}`)}
            className="px-2 py-0.5 rounded-lg text-[10px] transition-all hover:brightness-110"
            style={{ background: C.surfaceHigh, color: C.textMuted, border: `1px solid ${C.border}` }}
          >
            {provider.name} – {p}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold mb-1.5" style={{ color: C.textSub }}>Client / Label</label>
          <Input placeholder="e.g. Internal, ClientA" value={instanceLabel} onChange={setInstanceLabel} />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1.5" style={{ color: C.textSub }}>Environment</label>
          <select
            className="w-full text-xs px-3 py-2 rounded-xl outline-none"
            style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, color: C.text }}
            value={env}
            onChange={(e) => setEnv(e.target.value)}
          >
            {["Production", "Staging", "Development", "UAT", "All"].map((e) => (
              <option key={e} value={e} style={{ background: C.surfaceHigh }}>{e}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-2">
        {[
          { label: "Enable notifications immediately after setup",    desc: "Start routing alerts as soon as setup completes", default: true  },
          { label: "Notify on alert resolution",                        desc: "Send a follow-up message when an alert clears",   default: true  },
          { label: "Thread related alerts",                             desc: "Group repeat alerts in a single thread/ticket",   default: false },
        ].map((opt) => (
          <div key={opt.label} className="flex items-center justify-between p-3 rounded-xl" style={{ background: C.surfaceHigh, border: `1px solid ${C.border}` }}>
            <div>
              <div className="text-xs font-medium" style={{ color: C.text }}>{opt.label}</div>
              <div className="text-[10px]" style={{ color: C.textMuted }}>{opt.desc}</div>
            </div>
            <Toggle value={opt.default} onChange={() => {}} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Step: Test Connection ────────────────────────────────────────────────────
function StepTest({ provider, logs, setLogs, testState, setTestState }: {
  provider: ProviderConfig;
  logs: Array<{ time: string; level: "info"|"success"|"error"|"warn"; msg: string }>;
  setLogs: (l: typeof logs) => void;
  testState: "idle"|"running"|"success"|"error";
  setTestState: (s: "idle"|"running"|"success"|"error") => void;
}) {
  const runTest = () => {
    setTestState("running");
    setLogs([]);
    const logLines: typeof logs = [
      { time: "0ms",    level: "info",    msg: `Initializing test connection to ${provider.name}…` },
      { time: "45ms",   level: "info",    msg: "Resolving endpoint hostname" },
      { time: "82ms",   level: "info",    msg: "TCP connection established" },
      { time: "124ms",  level: "info",    msg: "TLS 1.3 handshake complete" },
      { time: "167ms",  level: "info",    msg: `Authenticating with ${provider.name} API` },
      { time: "234ms",  level: "success", msg: "Authentication successful" },
      { time: "278ms",  level: "info",    msg: provider.testAction },
      { time: "312ms",  level: "info",    msg: provider.authMethods[0] === "webhook" ? "HTTP POST → 204 No Content" : "HTTP 200 OK" },
      { time: "356ms",  level: "success", msg: "Test completed — connection is healthy" },
    ];
    let i = 0;
    const tick = () => {
      if (i >= logLines.length) { setTestState("success"); return; }
      // Snapshot the entry NOW — using `logLines[i]` inside the functional
      // updater would close over the mutable `i` and, once React flushes the
      // batch, `i` has already been incremented, pushing an undefined entry
      // on the final tick and crashing the render on `log.time`.
      const line = logLines[i];
      setLogs((prev: typeof logs) => [...prev, line]);
      i++;
      setTimeout(tick, 180 + Math.random() * 150);
    };
    tick();
  };

  const levelColor = { info: C.textSub, success: C.green, error: C.red, warn: C.amber };

  return (
    <div className="space-y-4" style={{ animation: "slide-up 0.25s ease" }}>
      <div className="text-xs leading-relaxed" style={{ color: C.textSub }}>
        CoreMonit will {provider.testAction.toLowerCase()} to verify the integration is working end-to-end.
      </div>

      <div className="rounded-xl overflow-hidden" style={{ background: "#030810", border: `1px solid ${C.border}` }}>
        <div className="flex items-center justify-between px-3 py-2" style={{ background: C.surfaceHigh, borderBottom: `1px solid ${C.border}` }}>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${testState === "running" ? "" : ""}`} style={{ background: testState === "running" ? C.blue : testState === "success" ? C.green : testState === "error" ? C.red : C.textMuted, animation: testState === "running" ? "pulse-dot 1s infinite" : undefined }} />
            <span className="text-[10px] font-mono" style={{ color: C.textMuted }}>
              {testState === "idle" ? "Ready" : testState === "running" ? "Running…" : testState === "success" ? "Passed" : "Failed"}
            </span>
          </div>
          <span className="text-[9px]" style={{ color: C.textMuted }}>Test Log</span>
        </div>
        <div className="p-3 h-48 overflow-auto space-y-1 font-mono">
          {logs.length === 0 && testState === "idle" && (
            <div className="text-[10px] text-center py-4" style={{ color: C.textMuted }}>Run the test to see output here</div>
          )}
          {logs.filter(Boolean).map((log, i) => (
            <div key={i} className="flex items-start gap-2 text-[10px]" style={{ animation: "slide-up 0.1s ease" }}>
              <span className="shrink-0 w-12 text-right" style={{ color: C.textMuted }}>{log.time}</span>
              <span className="shrink-0 w-12 uppercase" style={{ color: levelColor[log.level] }}>[{log.level}]</span>
              <span style={{ color: log.level === "success" ? C.green : log.level === "error" ? C.red : log.level === "warn" ? C.amber : C.textSub }}>
                {log.msg}
              </span>
            </div>
          ))}
        </div>
      </div>

      {testState === "success" && (
        <div className="flex items-center gap-2 p-3 rounded-xl" style={{ background: C.greenDim, border: "1px solid rgba(16,185,129,0.3)", animation: "slide-up 0.2s ease" }}>
          <CheckCircle size={14} style={{ color: C.green }} />
          <span className="text-xs font-medium" style={{ color: C.green }}>Test passed — integration is healthy and ready</span>
        </div>
      )}

      <Btn
        variant={testState === "success" ? "secondary" : "primary"}
        size="md"
        icon={testState === "running" ? <div className="w-3 h-3 border-2 border-white rounded-full border-t-transparent" style={{ animation: "spin 0.8s linear infinite" }} /> : <TestTube2 size={13} />}
        onClick={runTest}
        disabled={testState === "running"}
        className="w-full justify-center"
      >
        {testState === "idle" ? "Run Connection Test" : testState === "running" ? "Testing…" : "Run Again"}
      </Btn>
    </div>
  );
}

// ─── Step: Synchronization ────────────────────────────────────────────────────
function StepSync({ provider, syncItems, setSyncItems, syncing, setSyncing }: {
  provider: ProviderConfig;
  syncItems: Array<{ type: string; label: string; count: number; found: number; status: "pending"|"syncing"|"done" }>;
  setSyncItems: (items: typeof syncItems) => void;
  syncing: boolean; setSyncing: (v: boolean) => void;
}) {
  // Guard against re-entry: a double-click on "Start Synchronization" (or a
  // StrictMode double-invoke) would otherwise spawn parallel chains that
  // race on the same setSyncItems targets.
  const runningRef = useRef(false);

  const runSync = () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setSyncing(true);
    const initial = provider.syncResources.map((r) => ({ ...r, found: 0, status: "pending" as const }));
    setSyncItems(initial);
    let i = 0;
    const finish = () => { runningRef.current = false; setSyncing(false); };
    const syncOne = () => {
      if (i >= initial.length) { finish(); return; }
      // Snapshot the current row index into a fresh binding so it can't be
      // clobbered by `i++` before React flushes the batched updaters. The
      // previous code closed over the outer mutable `i` inside every
      // setSyncItems callback, so on the terminal interval tick — which
      // increments `i` before React ran the queued updaters — the "found"
      // and "done" updates were retargeted to the NEXT row, leaving the
      // current row stuck at its second-to-last count. See screenshot: on
      // Asana, Workspaces got stuck at 1/2, Projects at 12/14, Users at 28/32.
      const idx = i;
      const target = provider.syncResources[idx].count;
      setSyncItems((prev: typeof initial) => prev.map((r, j) => j === idx ? { ...r, status: "syncing" } : r));
      // Handle degenerate case of a zero-count resource — finish immediately.
      if (target <= 0) {
        setSyncItems((prev: typeof initial) => prev.map((r, j) => j === idx ? { ...r, status: "done", found: 0 } : r));
        i = idx + 1;
        setTimeout(syncOne, 200);
        return;
      }
      let count = 0;
      const counter = setInterval(() => {
        count = Math.min(count + Math.ceil(target / 8), target);
        const snap = count;
        if (snap >= target) {
          clearInterval(counter);
          setSyncItems((prev: typeof initial) => prev.map((r, j) => j === idx ? { ...r, status: "done", found: target } : r));
          i = idx + 1;
          setTimeout(syncOne, 300);
        } else {
          setSyncItems((prev: typeof initial) => prev.map((r, j) => j === idx ? { ...r, found: snap } : r));
        }
      }, 80);
    };
    syncOne();
  };

  const allDone = syncItems.length > 0 && syncItems.every((s) => s.status === "done");
  const stalled = syncItems.length > 0 && !syncing && !allDone;

  return (
    <div className="space-y-4" style={{ animation: "slide-up 0.25s ease" }}>
      {syncItems.length === 0 ? (
        <div className="text-center py-6 space-y-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto" style={{ background: C.blueDim, border: `1px solid ${C.borderBright}` }}>
            <RefreshCw size={22} style={{ color: C.blue }} />
          </div>
          <div>
            <div className="text-sm font-semibold" style={{ color: C.text }}>Ready to synchronize</div>
            <div className="text-xs mt-1" style={{ color: C.textMuted }}>
              CoreMonit will fetch {provider.syncResources.map((r) => r.label.toLowerCase()).join(", ")} from {provider.name}.
            </div>
          </div>
          <Btn variant="primary" size="md" icon={<RefreshCw size={13} />} onClick={runSync} className="mx-auto">Start Synchronization</Btn>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {syncItems.map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-3 rounded-xl transition-all duration-300"
                style={{
                  background: item.status === "done" ? C.greenDim : item.status === "syncing" ? C.blueDim : C.surfaceHigh,
                  border: `1px solid ${item.status === "done" ? "rgba(16,185,129,0.25)" : item.status === "syncing" ? C.borderBright : C.border}`,
                  opacity: item.status === "pending" ? 0.5 : 1,
                }}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: item.status === "done" ? C.green : item.status === "syncing" ? C.blue : C.surfaceHigh }}>
                  {item.status === "done"    && <Check size={14} color="#fff" />}
                  {item.status === "syncing" && <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent" style={{ animation: "spin 0.7s linear infinite" }} />}
                  {item.status === "pending" && <Server size={12} style={{ color: C.textMuted }} />}
                </div>
                <div className="flex-1">
                  <div className="text-xs font-medium" style={{ color: item.status === "done" ? C.green : item.status === "syncing" ? C.blue : C.textMuted }}>
                    {item.label}
                  </div>
                  {(item.status === "syncing" || item.status === "done") && (
                    <div className="text-[10px]" style={{ color: C.textMuted }}>
                      {item.status === "syncing" ? `Fetching… (${item.found} found so far)` : `${item.found} ${item.label.toLowerCase()} synced`}
                    </div>
                  )}
                </div>
                {item.status === "done" && (
                  <span className="text-sm font-bold" style={{ color: C.green }}>{item.found}</span>
                )}
              </div>
            ))}
          </div>

          {allDone && !syncing && (
            <div className="grid grid-cols-3 gap-2" style={{ animation: "slide-up 0.3s ease" }}>
              {syncItems.map((item) => (
                <div key={item.type} className="text-center p-3 rounded-xl" style={{ background: C.surfaceHigh, border: `1px solid ${C.border}` }}>
                  <div className="text-2xl font-bold" style={{ color: provider.color }}>{item.found}</div>
                  <div className="text-[10px] mt-0.5" style={{ color: C.textMuted }}>{item.label}</div>
                </div>
              ))}
            </div>
          )}

          {stalled && (
            <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: C.amberDim, border: "1px solid rgba(245,158,11,0.3)" }}>
              <div className="flex items-center gap-2">
                <AlertTriangle size={14} style={{ color: C.amber }} />
                <span className="text-xs" style={{ color: C.amber }}>Some resources did not finish syncing.</span>
              </div>
              <Btn variant="secondary" size="sm" icon={<RefreshCw size={12} />} onClick={runSync}>Retry Sync</Btn>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Step: Review ─────────────────────────────────────────────────────────────
function StepReview({ provider, instanceName, instanceLabel, env, selectedAuth, syncItems, checks }: {
  provider: ProviderConfig; instanceName: string; instanceLabel: string; env: string;
  selectedAuth: AuthMethod;
  syncItems: Array<{ label: string; found: number }>;
  checks: Array<{ label: string; status: string }>;
}) {
  const authLabels: Record<AuthMethod, string> = { oauth: "OAuth 2.0", token: "API Token", webhook: "Incoming Webhook", smtp: "SMTP", iam: "IAM Role", "service-account": "Service Account", app: "GitHub App", multi: "Multi-method" };
  const passed = checks.filter((c) => c.status === "passed" || c.status === "warning").length;

  return (
    <div className="space-y-4" style={{ animation: "slide-up 0.25s ease" }}>
      <div className="p-3 rounded-xl flex items-center gap-3" style={{ background: C.greenDim, border: "1px solid rgba(16,185,129,0.3)" }}>
        <CheckCircle size={16} style={{ color: C.green }} />
        <span className="text-xs font-semibold" style={{ color: C.green }}>All checks passed · Ready to activate</span>
      </div>

      {/* Summary */}
      <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${C.border}` }}>
        {[
          { label: "Integration",       value: `${provider.icon} ${provider.name}`, color: provider.color },
          { label: "Connection Name",   value: instanceName || `${provider.name} Instance` },
          { label: "Label",             value: instanceLabel || "—" },
          { label: "Environment",       value: env },
          { label: "Auth Method",       value: authLabels[selectedAuth] },
          { label: "Permissions",       value: `${passed} of ${checks.length} checks passed` },
          { label: "Synced Resources",  value: syncItems.map((s) => `${s.found} ${s.label}`).join(", ") || "—" },
          { label: "Health Status",     value: "All systems operational", color: C.green },
        ].map((row, i) => (
          <div
            key={row.label}
            className="flex items-center gap-3 px-4 py-2.5"
            style={{ borderBottom: i < 7 ? `1px solid ${C.border}` : "none", background: i % 2 === 0 ? C.surfaceHigh : "transparent" }}
          >
            <span className="text-xs w-28 shrink-0" style={{ color: C.textMuted }}>{row.label}</span>
            <span className="text-xs font-medium" style={{ color: row.color ?? C.text }}>{row.value}</span>
          </div>
        ))}
      </div>

      {/* Warning */}
      {checks.some((c) => c.status === "warning") && (
        <div className="flex items-start gap-2 p-3 rounded-xl" style={{ background: C.amberDim, border: "1px solid rgba(245,158,11,0.2)" }}>
          <AlertTriangle size={13} style={{ color: C.amber, flexShrink: 0 }} />
          <span className="text-xs" style={{ color: C.amber }}>
            Some optional permissions are missing. Core functionality will work, but some advanced features may be limited.
          </span>
        </div>
      )}

      <div className="text-xs text-center" style={{ color: C.textMuted }}>
        Click <strong style={{ color: C.text }}>Activate Integration</strong> to make this connection live. You can disable or reconfigure it at any time.
      </div>
    </div>
  );
}

// ─── Step: Done ───────────────────────────────────────────────────────────────
function StepDone({ provider, instanceName }: { provider: ProviderConfig; instanceName: string }) {
  return (
    <div className="flex flex-col items-center text-center py-6 space-y-5" style={{ animation: "slide-up 0.3s ease" }}>
      <div
        className="w-20 h-20 rounded-3xl flex items-center justify-center"
        style={{
          background: `linear-gradient(135deg, ${provider.color}30, ${provider.color}10)`,
          border: `2px solid ${provider.color}50`,
          boxShadow: `0 0 40px ${provider.color}30`,
          animation: "glow-breathe 3s ease-in-out infinite",
        }}
      >
        <span className="text-4xl">{provider.icon}</span>
      </div>
      <div>
        <div className="text-lg font-bold" style={{ color: C.text }}>{provider.name} is now connected!</div>
        <div className="text-sm mt-1" style={{ color: C.textMuted }}>
          <strong style={{ color: provider.color }}>{instanceName || provider.name}</strong> is active and ready to receive alerts.
        </div>
      </div>

      {/* Health badge */}
      <div className="flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: C.greenDim, border: "1px solid rgba(16,185,129,0.3)" }}>
        <div className="w-2 h-2 rounded-full" style={{ background: C.green, animation: "pulse-dot 2s infinite" }} />
        <span className="text-xs font-semibold" style={{ color: C.green }}>Connected · All systems operational</span>
      </div>

      {/* Next steps */}
      <div className="w-full space-y-2 text-left">
        <div className="text-xs font-semibold" style={{ color: C.textSub }}>Recommended Next Steps</div>
        {[
          { label: "Create a routing rule",    desc: "Route specific alerts to this integration",    path: "/system/notification-center/routing-studio",  icon: <Zap size={12} style={{ color: C.blue }} />   },
          { label: "Test a notification",      desc: "Send a sample alert to verify delivery",       path: "/system/notification-center/testing",           icon: <TestTube2 size={12} style={{ color: C.green }} /> },
          { label: "View the setup guide",     desc: "Learn advanced configuration options",         path: "/system/integrations/slack/help",                            icon: <BookOpen size={12} style={{ color: C.cyan }} />  },
        ].map((step) => (
          <div
            key={step.label}
            className="flex items-center gap-3 p-3 rounded-xl cursor-pointer hover:brightness-110 transition-all"
            style={{ background: C.surfaceHigh, border: `1px solid ${C.border}` }}
          >
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: C.bg }}>{step.icon}</div>
            <div className="flex-1">
              <div className="text-xs font-semibold" style={{ color: C.text }}>{step.label}</div>
              <div className="text-[10px]" style={{ color: C.textMuted }}>{step.desc}</div>
            </div>
            <ChevronRight size={12} style={{ color: C.textMuted }} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Wizard ──────────────────────────────────────────────────────────────
interface ConnectionWizardProps {
  provider: ProviderConfig;
  onClose: () => void;
  onComplete: (name: string, label: string, env: string) => void;
}

export function ConnectionWizard({ provider, onClose, onComplete }: ConnectionWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completed, setCompleted] = useState<Set<number>>(new Set());

  // Auth state
  const [selectedAuth, setSelectedAuth] = useState<AuthMethod>(provider.preferredAuth);
  const [token, setToken] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [smtpConfig, setSmtpConfig] = useState<Record<string, string>>({});
  const [authStatus, setAuthStatus] = useState<string>("idle");

  // Validation state
  const [validationChecks, setValidationChecks] = useState<CheckItem[]>([]);
  const [validating, setValidating] = useState(false);

  // Config state
  const [instanceName, setInstanceName] = useState("");
  const [instanceLabel, setInstanceLabel] = useState("");
  const [env, setEnv] = useState("Production");

  // Test state
  const [testLogs, setTestLogs] = useState<Array<{ time: string; level: "info"|"success"|"error"|"warn"; msg: string }>>([]);
  const [testState, setTestState] = useState<"idle"|"running"|"success"|"error">("idle");

  // Sync state
  const [syncItems, setSyncItems] = useState<Array<{ type: string; label: string; count: number; found: number; status: "pending"|"syncing"|"done" }>>([]);
  const [syncing, setSyncing] = useState(false);

  const canContinue = () => {
    const step = STEPS[currentStep].id;
    if (step === "overview")  return true;
    if (step === "auth")      return authStatus === "success" && (token.length > 8 || webhookUrl.startsWith("http") || Object.keys(smtpConfig).length > 0);
    if (step === "validate")  return validationChecks.length > 0 && !validating && validationChecks.every((c) => c.status !== "pending" && c.status !== "running");
    if (step === "configure") return instanceName.length > 0;
    if (step === "test")      return testState === "success";
    if (step === "sync")      return syncItems.length > 0 && !syncing && syncItems.every((s) => s.status === "done");
    if (step === "review")    return true;
    return true;
  };

  const advance = () => {
    if (currentStep === STEPS.length - 1) {
      onComplete(instanceName || `${provider.name} Instance`, instanceLabel, env);
      return;
    }
    setCompleted((prev) => new Set([...prev, currentStep]));
    setCurrentStep((s) => s + 1);
  };

  const stepId = STEPS[currentStep].id;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(7,13,26,0.88)", backdropFilter: "blur(12px)", animation: "slide-up 0.2s ease" }}
    >
      <div
        className="w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden"
        style={{
          background: C.surface,
          border: `1px solid ${C.borderBright}`,
          borderRadius: 22,
          boxShadow: `0 24px 80px rgba(0,0,0,0.6), 0 0 60px rgba(45,127,249,0.1)`,
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0" style={{ borderColor: C.border }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center text-base" style={{ background: `${provider.color}20`, border: `1px solid ${provider.color}30` }}>
              {provider.icon}
            </div>
            <div>
              <div className="text-sm font-bold" style={{ color: C.text }}>Connect {provider.name}</div>
              <div className="text-[10px]" style={{ color: C.textMuted }}>{provider.category} · {provider.estimatedSetup} estimated</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 transition-colors">
            <X size={14} style={{ color: C.textSub }} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 shrink-0" style={{ background: C.surfaceHigh }}>
          <div
            className="h-full transition-all duration-500"
            style={{
              width: `${((currentStep + (completed.has(currentStep) ? 1 : 0)) / STEPS.length) * 100}%`,
              background: `linear-gradient(90deg, ${C.blue}, ${C.cyan})`,
            }}
          />
        </div>

        {/* Body */}
        <div className="flex-1 flex overflow-hidden min-h-0">
          <StepSidebar current={currentStep} completed={completed} />

          {/* Main content */}
          <div className="flex-1 overflow-auto p-6">
            <div className="mb-4">
              <h2 className="text-sm font-bold" style={{ color: C.text }}>{STEPS[currentStep].label}</h2>
              <div className="text-[10px] mt-0.5" style={{ color: C.textMuted }}>Step {currentStep + 1} of {STEPS.length}</div>
            </div>

            {stepId === "overview"  && <StepOverview provider={provider} selectedAuth={selectedAuth} setSelectedAuth={setSelectedAuth} />}
            {stepId === "auth"      && <StepAuth provider={provider} selectedAuth={selectedAuth} token={token} setToken={setToken} webhookUrl={webhookUrl} setWebhookUrl={setWebhookUrl} smtpConfig={smtpConfig} setSmtpConfig={setSmtpConfig} authStatus={authStatus} setAuthStatus={setAuthStatus} />}
            {stepId === "validate"  && <StepValidate provider={provider} checks={validationChecks} setChecks={setValidationChecks} validating={validating} setValidating={setValidating} />}
            {stepId === "configure" && <StepConfigure provider={provider} instanceName={instanceName} setInstanceName={setInstanceName} instanceLabel={instanceLabel} setInstanceLabel={setInstanceLabel} env={env} setEnv={setEnv} />}
            {stepId === "test"      && <StepTest provider={provider} logs={testLogs} setLogs={setTestLogs} testState={testState} setTestState={setTestState} />}
            {stepId === "sync"      && <StepSync provider={provider} syncItems={syncItems} setSyncItems={setSyncItems} syncing={syncing} setSyncing={setSyncing} />}
            {stepId === "review"    && <StepReview provider={provider} instanceName={instanceName} instanceLabel={instanceLabel} env={env} selectedAuth={selectedAuth} syncItems={syncItems} checks={validationChecks} />}
            {stepId === "done"      && <StepDone provider={provider} instanceName={instanceName} />}
          </div>
        </div>

        {/* Footer nav */}
        <div className="flex items-center justify-between px-6 py-4 border-t shrink-0" style={{ borderColor: C.border, background: `${C.bg}88` }}>
          <div className="flex items-center gap-2">
            <Btn variant="ghost" size="sm" onClick={() => currentStep > 0 ? setCurrentStep((s) => s - 1) : onClose()}>
              {currentStep === 0 ? "Cancel" : "← Back"}
            </Btn>
            {stepId !== "done" && (
              <button
                onClick={() => { onComplete("", "", ""); }}
                className="text-xs"
                style={{ color: C.textMuted }}
              >
                Save draft &amp; continue later
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {stepId !== "done" && (
              <div className="text-[10px] mr-2" style={{ color: C.textMuted }}>
                {completed.size} / {STEPS.length - 1} steps complete
              </div>
            )}
            <Btn
              variant={stepId === "review" ? "glow" : "primary"}
              size="md"
              icon={stepId === "done" ? undefined : stepId === "review" ? <Zap size={13} /> : <ChevronRight size={13} />}
              onClick={advance}
              disabled={!canContinue()}
            >
              {stepId === "done"   ? "Go to Integration" :
               stepId === "review" ? "Activate Integration" :
               "Continue →"}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}
